import React, { useState } from 'react';

import { Button, ClickableIcon, LookupStatusBadge, TooltipPortal } from '~components';
import Icon, {
  faChevronDown,
  faChevronUp,
  faPenToSquare,
  faPlus,
  faTrash,
  faTriangleExclamation,
} from '~components/icon';

import { CarePlanGoal, CarePlanLookup, CarePlanNeed, CarePlanServiceLine } from '~clients/models';

import GoalNumberAvatar from '../goals/GoalNumberAvatar';
import LookupBadge from '../goals/LookupBadge';
import { bySequence } from '../reorder';

interface ComponentProps {
  goals: CarePlanGoal[];
  serviceLines: CarePlanServiceLine[];
  serviceTypesById: Map<number, string>;
  frequenciesById: Map<string, string>;
  needsById: Map<string, CarePlanNeed>;
  statusesById: Map<string, CarePlanLookup>;
  approachTypesById: Map<string, CarePlanLookup>;
  onAddForGoal: (goalId: string | null) => void;
  onEdit: (line: CarePlanServiceLine) => void;
  onDelete: (line: CarePlanServiceLine) => void;
  isSaving: boolean;
}

interface ServiceCardProps {
  line: CarePlanServiceLine;
  serviceTypesById: Map<number, string>;
  frequenciesById: Map<string, string>;
  onEdit: (line: CarePlanServiceLine) => void;
  onDelete: (line: CarePlanServiceLine) => void;
  isSaving: boolean;
}

// A single service, with a collapsible "How we will meet this need" notes section.
const ServiceCard: React.FC<ServiceCardProps> = ({
  line,
  serviceTypesById,
  frequenciesById,
  onEdit,
  onDelete,
  isSaving,
}) => {
  const [expanded, setExpanded] = useState(false);

  const serviceName = serviceTypesById.get(line.ext_service_type_id) ?? `Service type #${line.ext_service_type_id}`;
  const frequencyName = line.frequency_id ? frequenciesById.get(line.frequency_id) : undefined;

  return (
    <div className="care-plan-service-card" data-cy={`care-plan-service-${line.id}`}>
      <div className="care-plan-service-body">
        <div className="care-plan-service-heading">
          {line.notes && (
            <button
              type="button"
              className="care-plan-service-notes-toggle"
              aria-expanded={expanded}
              aria-label={expanded ? 'Hide delivery notes' : 'Show delivery notes'}
              data-cy={`care-plan-service-notes-toggle-${line.id}`}
              onClick={() => setExpanded((open) => !open)}
            >
              <Icon icon={expanded ? faChevronUp : faChevronDown} aria-hidden />
            </button>
          )}
          <span className="fw-bold">{serviceName}</span>
          {frequencyName && <span className="care-plan-service-frequency text-muted">{frequencyName}</span>}
          <LookupStatusBadge
            status={{
              tag: line.is_active ? 'active' : 'inactive',
              name: line.is_active ? 'Active' : 'Inactive',
              // App success/danger — dark backgrounds, so the badge renders white text.
              colour: line.is_active ? '198754' : 'dc3545',
            }}
            dataCy={`care-plan-service-status-${line.id}`}
          />
        </div>
        {line.notes && expanded && (
          <div className="care-plan-service-notes" data-cy={`care-plan-service-notes-${line.id}`}>
            {line.notes}
          </div>
        )}
      </div>
      <div className="care-plan-service-actions">
        <ClickableIcon
          id={`care-plan-service-edit-${line.id}`}
          dataCy={`care-plan-service-edit-${line.id}`}
          icon={faPenToSquare}
          color="secondary"
          toolTip="Edit service"
          ariaLabel={`Edit ${serviceName}`}
          onClick={() => onEdit(line)}
        />
        <ClickableIcon
          id={`care-plan-service-delete-${line.id}`}
          dataCy={`care-plan-service-delete-${line.id}`}
          icon={faTrash}
          color="danger"
          disabled={isSaving}
          toolTip="Delete service"
          ariaLabel={`Delete ${serviceName}`}
          onClick={() => onDelete(line)}
        />
      </div>
    </div>
  );
};

/**
 * "By goal" view: services grouped under their goal, an "Unlinked services" section for
 * goal_id = null, and red warning cards for goals with no services.
 */
const ByGoalView: React.FC<ComponentProps> = ({
  goals,
  serviceLines,
  serviceTypesById,
  frequenciesById,
  needsById,
  statusesById,
  approachTypesById,
  onAddForGoal,
  onEdit,
  onDelete,
  isSaving,
}) => {
  const orderedGoals = bySequence(goals);
  const linesForGoal = (goalId: string) => bySequence(serviceLines.filter((line) => line.goal_id === goalId));
  const unlinkedServices = bySequence(serviceLines.filter((line) => !line.goal_id));

  const goalsWithServices = orderedGoals.filter((goal) => linesForGoal(goal.id).length > 0);
  const goalsWithoutServices = orderedGoals.filter((goal) => linesForGoal(goal.id).length === 0);

  const renderGoalHeader = (goal: CarePlanGoal, number: number) => {
    const need = goal.need_id ? needsById.get(goal.need_id) : undefined;
    const status = goal.status_id ? statusesById.get(goal.status_id) : undefined;
    const approachType = goal.approach_type_id ? approachTypesById.get(goal.approach_type_id) : undefined;

    return (
      <div className="care-plan-service-goal-header">
        <GoalNumberAvatar number={number} />
        <div className="care-plan-service-goal-heading">
          <div className="care-plan-service-goal-title">
            <span className="fw-bold">{goal.description}</span>
            {approachType && (
              <LookupBadge lookup={approachType} dataCy={`care-plan-service-goal-approach-${goal.id}`} />
            )}
            {status && <LookupBadge lookup={status} dataCy={`care-plan-service-goal-status-${goal.id}`} />}
          </div>
          {need && (
            <div
              className="care-plan-service-goal-need text-muted small"
              data-cy={`care-plan-service-goal-need-${goal.id}`}
            >
              Linked need: {need.title || need.description}
            </div>
          )}
        </div>
        <Button
          id={`care-plan-service-add-for-goal-${goal.id}`}
          size="sm"
          color="success"
          className="care-plan-service-goal-add"
          dataCy={`care-plan-service-add-for-goal-${goal.id}`}
          aria-label={`Add service to goal ${number}`}
          onClick={() => onAddForGoal(goal.id)}
        >
          <Icon icon={faPlus} />
        </Button>
        <TooltipPortal targetId={`care-plan-service-add-for-goal-${goal.id}`} content="Add Goal Service" />
      </div>
    );
  };

  if (serviceLines.length === 0) {
    return (
      <div className="care-plan-service-by-goal" data-cy="care-plan-service-by-goal">
        <p className="text-muted care-plan-services-empty" data-cy="care-plan-services-empty">
          No services recorded.
        </p>
      </div>
    );
  }

  return (
    <div className="care-plan-service-by-goal" data-cy="care-plan-service-by-goal">
      {goalsWithServices.map((goal) => {
        // Number by position in the full ordered goal set so it matches the Goals tab.
        const number = orderedGoals.indexOf(goal) + 1;
        return (
          <div
            key={goal.id}
            className="care-plan-service-goal-group"
            data-cy={`care-plan-service-goal-group-${goal.id}`}
          >
            {renderGoalHeader(goal, number)}
            <div className="care-plan-service-cards">
              {linesForGoal(goal.id).map((line) => (
                <ServiceCard
                  key={line.id}
                  line={line}
                  serviceTypesById={serviceTypesById}
                  frequenciesById={frequenciesById}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  isSaving={isSaving}
                />
              ))}
            </div>
          </div>
        );
      })}

      {unlinkedServices.length > 0 && (
        <div className="care-plan-service-goal-group care-plan-service-unlinked" data-cy="care-plan-service-unlinked">
          <div className="care-plan-service-goal-header">
            <Icon icon={faTriangleExclamation} className="care-plan-service-warning-icon" aria-hidden />
            <div className="care-plan-service-goal-heading">
              <span className="fw-bold">Unlinked services</span>
              <div className="care-plan-service-goal-need text-muted small">
                Link each service to a goal. The plan cannot be activated while unlinked services remain.
              </div>
            </div>
          </div>
          <div className="care-plan-service-cards">
            {unlinkedServices.map((line) => (
              <ServiceCard
                key={line.id}
                line={line}
                serviceTypesById={serviceTypesById}
                frequenciesById={frequenciesById}
                onEdit={onEdit}
                onDelete={onDelete}
                isSaving={isSaving}
              />
            ))}
          </div>
        </div>
      )}

      {goalsWithoutServices.length > 0 && (
        <div className="care-plan-service-warnings" data-cy="care-plan-service-warnings">
          {goalsWithoutServices.map((goal) => {
            const number = orderedGoals.indexOf(goal) + 1;
            return (
              <div
                key={goal.id}
                className="care-plan-service-warning-card"
                data-cy={`care-plan-service-warning-${goal.id}`}
              >
                <div className="care-plan-service-warning-body">
                  <Icon icon={faTriangleExclamation} className="care-plan-service-warning-icon" aria-hidden />
                  <div>
                    <div className="fw-bold">
                      Goal {number}: {goal.description}
                    </div>
                    <div className="text-muted small">No services linked to this goal yet.</div>
                  </div>
                </div>
                <Button
                  id={`care-plan-service-add-for-goal-${goal.id}`}
                  size="sm"
                  color="success"
                  dataCy={`care-plan-service-add-for-goal-${goal.id}`}
                  aria-label={`Add service to goal ${number}`}
                  onClick={() => onAddForGoal(goal.id)}
                >
                  <Icon icon={faPlus} />
                </Button>
                <TooltipPortal targetId={`care-plan-service-add-for-goal-${goal.id}`} content="Add Goal Service" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ByGoalView;
